import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { GraduationCap, Brain, BarChart3, Users, FileCheck, Sparkles, Shield, Key } from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleParentTeacherLogin = () => {
    // Redirect to root so hash fragment with session_id can be processed
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleStudentLogin = () => {
    navigate("/student-login");
  };

  return (
    <div className="min-h-screen bg-white">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100" role="banner">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center" aria-hidden="true">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <span className="font-semibold text-xl tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              MathTrack
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={handleStudentLogin}
              variant="outline"
              data-testid="header-student-login-btn"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
              aria-label="Student login with access code"
              tabIndex={0}
            >
              <Key className="w-4 h-4 mr-2" aria-hidden="true" />
              I'm a Student
            </Button>
            <Button 
              onClick={handleParentTeacherLogin}
              data-testid="header-login-btn"
              className="bg-primary hover:bg-primary/90 text-white px-6 rounded-md"
              aria-label="Parent or Teacher sign in with Google"
              tabIndex={0}
            >
              Parent / Teacher Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="main-content" className="pt-32 pb-20 px-6 graph-paper-bg" role="main" tabIndex={-1}>
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Shield className="w-4 h-4" aria-hidden="true" />
                COPPA Compliant - Safe for Kids
              </div>
              <h1 
                className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Master Math Kangaroo with{' '}
                <span className="text-primary">Smart Analytics</span>
              </h1>
              <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-lg">
                Upload handwritten answer sheets, get instant grading with AI, and discover exactly where to improve with concept-based weakness analysis.
              </p>
              
              {/* Two Login Options */}
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5" aria-hidden="true" />
                    For Students
                  </h3>
                  <p className="text-sm text-blue-700 mb-3">Already have an access code from your parent?</p>
                  <Button 
                    onClick={handleStudentLogin}
                    data-testid="hero-student-login-btn"
                    className="bg-blue-600 hover:bg-blue-700 text-white w-full"
                    aria-label="Student login with access code"
                    tabIndex={0}
                  >
                    <Key className="w-4 h-4 mr-2" aria-hidden="true" />
                    Enter Access Code
                  </Button>
                </div>
                
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                  <h3 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                    <Users className="w-5 h-5" aria-hidden="true" />
                    For Parents & Teachers
                  </h3>
                  <p className="text-sm text-green-700 mb-3">Create student accounts and monitor progress</p>
                  <Button 
                    onClick={handleParentTeacherLogin}
                    data-testid="hero-parent-login-btn"
                    className="bg-green-600 hover:bg-green-700 text-white w-full"
                    aria-label="Parent or Teacher sign in with Google"
                    tabIndex={0}
                  >
                    Sign In with Google
                  </Button>
                </div>
              </div>
            </div>
            <div className="relative animate-fade-in stagger-2">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl shadow-primary/10 border border-gray-200">
                <img 
                  src="https://images.pexels.com/photos/4087576/pexels-photo-4087576.jpeg"
                  alt="Student working on math problems at a desk with papers and pencil"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" aria-hidden="true">
                    <BarChart3 className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Average Improvement</p>
                    <p className="text-2xl font-bold text-gray-900 font-mono">+23%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-6 bg-gray-50 border-y border-gray-200" aria-labelledby="how-it-works">
        <div className="max-w-5xl mx-auto">
          <h2 
            id="how-it-works"
            className="text-3xl font-bold text-gray-900 text-center mb-12"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">1</div>
              <h3 className="font-semibold text-gray-900 mb-2">Parent Registers</h3>
              <p className="text-gray-600 text-sm">Sign in with Google and create accounts for your children</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">2</div>
              <h3 className="font-semibold text-gray-900 mb-2">Share Access Code</h3>
              <p className="text-gray-600 text-sm">Give your child their simple 4-character code (e.g., 5LD9)</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">3</div>
              <h3 className="font-semibold text-gray-900 mb-2">Start Learning</h3>
              <p className="text-gray-600 text-sm">Student logs in, uploads answers, and tracks progress</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6 bg-white" aria-labelledby="features-heading">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-fade-in">
            <h2 
              id="features-heading"
              className="text-4xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Everything You Need to Excel
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Powerful tools for students, parents, and teachers to track progress and identify areas for improvement.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" role="list" aria-label="Features">
            {[
              {
                icon: FileCheck,
                title: "AI Answer Extraction",
                description: "Upload handwritten scantron sheets and let AI accurately extract and grade your answers.",
                color: "bg-blue-100 text-blue-700"
              },
              {
                icon: Brain,
                title: "Weakness Analysis",
                description: "Identify areas to improve like Algebra, Geometry, or Logic with AI-powered insights.",
                color: "bg-purple-100 text-purple-700"
              },
              {
                icon: BarChart3,
                title: "Progress Tracking",
                description: "Track scores over time with clear charts and see your improvement journey.",
                color: "bg-green-100 text-green-700"
              },
              {
                icon: Users,
                title: "Parent Dashboard",
                description: "Parents create student accounts and monitor their children's progress safely.",
                color: "bg-orange-100 text-orange-700",
                action: handleParentTeacherLogin
              },
              {
                icon: GraduationCap,
                title: "Teacher Dashboard",
                description: "Teachers manage answer keys and view anonymized student results securely.",
                color: "bg-pink-100 text-pink-700",
                action: handleParentTeacherLogin
              },
              {
                icon: Shield,
                title: "Privacy First",
                description: "COPPA, FERPA & SOPIPA compliant. No personal info collected from children.",
                color: "bg-green-100 text-green-700"
              }
            ].map((feature, index) => (
              <div 
                key={feature.title}
                className={`bg-white border border-gray-200 rounded-xl p-6 card-hover animate-fade-in stagger-${index % 4 + 1} ${feature.action ? 'cursor-pointer hover:border-primary/50 transition-colors' : ''}`}
                role={feature.action ? "button" : "listitem"}
                tabIndex={0}
                onClick={feature.action}
                onKeyDown={(e) => {
                  if (feature.action && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    feature.action();
                  }
                }}
              >
                <div className={`w-12 h-12 ${feature.color} rounded-lg flex items-center justify-center mb-4`} aria-hidden="true">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-gray-700">
                  {feature.description}
                </p>
                {feature.action && (
                  <div className="mt-4 flex items-center text-primary font-medium text-sm">
                    Go to Dashboard <Users className="w-4 h-4 ml-1" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-6" role="contentinfo">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center" aria-hidden="true">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-white">MathTrack</span>
          </div>
          <div className="text-center md:text-right">
            <p className="text-sm">© 2024 MathTrack. Built for Math Kangaroo Excellence.</p>
            <p className="text-xs mt-1">COPPA • FERPA • SOPIPA • WCAG 2.1 AA Compliant</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
